/**
 * Date formats for the appointment calendar's column headers, and the height of
 * the toolbar above it.
 *
 * DHTMLX sizes a scale cell by dividing the container, never by what the label
 * needs, and it neither wraps nor ellipsises — a label that does not fit spills
 * over its neighbour, or is clipped mid-word when the text is centred. So the
 * format has to be chosen for the width, which is what this module is.
 *
 * Every number here was measured in a browser at the width it names, against
 * this app's own stylesheet, with a text-width probe. `scrollWidth` is NOT a
 * usable check for these cells: the label is centred, so it is clipped equally
 * on both sides and scrollWidth stays equal to clientWidth while the text is
 * visibly cut. That false negative is why "Mon 14" shipped as "Mon" at 390px in
 * the first version of this fix.
 *
 * Formats are DHTMLX date_to_str patterns:
 *   %D short day name ("Mon")        %l full day name ("Monday")
 *   %F month name ("September")      %j day of month, no leading zero
 */

/** The shared phone breakpoint (hooks/useIsMobile.js), as a number. */
export const MOBILE_MAX = 767;

/**
 * Below this, a week column is 40px or less and "Mon 14" cannot fit at any
 * readable size — 41px is needed at 12px, and 38px even at 11px. Measured:
 * fits at 375px (42px column) and above, fails at 360px by a single pixel.
 */
export const NARROW_MAX = 374;

export const SCALE_FORMATS = {
  // DHTMLX's own defaults, restated so the desktop path is explicit rather than
  // "whatever the library happens to do" — and so an upgrade that moves them
  // fails a test instead of quietly changing the calendar.
  desktop: { week: '%D, %F %j', month: '%l' },
  // "Mon 14" / "Mon". The week scale keeps the date number: without it a phone
  // user cannot tell which day of the month a column is.
  mobile: { week: '%D %j', month: '%D' },
  // On the narrowest phones the number is what has to go. The week range in the
  // toolbar ("14-20 Sep 2026") still says which week is on screen.
  narrow: { week: '%D', month: '%D' },
};

export function scaleTier(width) {
  if (width > MOBILE_MAX) return 'desktop';
  return width <= NARROW_MAX ? 'narrow' : 'mobile';
}

export default function scaleFormats(width) {
  return SCALE_FORMATS[scaleTier(width)];
}

/**
 * Height of the toolbar above the calendar.
 *
 * DHTMLX lays that toolbar out as one row of absolutely positioned children and
 * never checks whether they fit. At 390px they did not: the date spanned the
 * full width with all three view tabs painted on top of it, and the month tab
 * also sat over the prev and Today buttons. The phone layout (index.css) gives
 * it two rows — date and navigation above, view tabs below — and that needs
 * more height than the library's 59px.
 *
 * It has to be set in JS rather than CSS because DHTMLX writes the height
 * inline and derives the top offset of the header and the grid from this same
 * number. Setting it here keeps all three in step; setting it in CSS would move
 * the bar and leave the grid behind, overlapping it.
 */
export const NAV_HEIGHT = { mobile: 108, desktop: 59 };

export function navHeight(width) {
  return width > MOBILE_MAX ? NAV_HEIGHT.desktop : NAV_HEIGHT.mobile;
}

/**
 * The week range shown in that toolbar, shortened for a phone.
 *
 * "14 Sep 2026 – 20 Sep 2026" needs 184px and the phone row leaves 136px at
 * 320px and 176px at 360px, so it was clipped on any small phone. Repeating the
 * year, and the month when both ends share it, is what costs the room.
 *
 * The dash carries no spaces deliberately: on a week that crosses a month
 * boundary at 320px, those two spaces are exactly the difference between
 * fitting and being cut off.
 *
 * `dateToStr` is DHTMLX's date_to_str factory, passed in so this stays a pure
 * function that a test can drive without the library.
 */
export function compactWeekRange(start, last, dateToStr) {
  const sameMonth =
    start.getMonth() === last.getMonth() && start.getFullYear() === last.getFullYear();
  const from = dateToStr(sameMonth ? '%j' : '%j %M')(start);
  const to = dateToStr('%j %M %Y')(last);
  return `${from}–${to}`;
}
